/**
 * Avatar upload helper (spec §8.8; bucket policy: public read, owner write).
 *
 * The 'avatars' bucket is public-read, so a public URL is safe for display.
 * Objects are stored under <userId>/ so storage policies can enforce that a
 * user can only write within their own prefix. The profile row stores the
 * object path (Profile.avatarPath); the public URL is derived at display time.
 *
 * When Supabase is not configured the typed SUPABASE_NOT_CONFIGURED AppError
 * propagates — the UI shows the honest local-draft state, never a fake upload.
 */
import { BUCKETS, getSupabase, requireSupabase } from '@/lib/supabase/client'
import { makeAppError, newCorrelationId, toAppError } from '@/lib/supabase/errors'
import type { AppError } from '@/types'

export const AVATAR_MAX_BYTES = 2 * 1024 * 1024 // 2 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const

function extensionFor(file: File): string {
  const fromType = file.type.split('/')[1]
  if (fromType === 'jpeg') return 'jpg'
  if (fromType && /^[a-z0-9]+$/.test(fromType)) return fromType
  const fromName = file.name.split('.').pop()
  return fromName && /^[a-z0-9]+$/i.test(fromName) ? fromName.toLowerCase() : 'jpg'
}

/** Validates the file; returns a Dutch user-facing AppError or null. */
export function validateAvatarFile(file: File): AppError | null {
  if (!ACCEPTED_TYPES.includes(file.type as (typeof ACCEPTED_TYPES)[number])) {
    return makeAppError({
      code: 'AVATAR_TYPE_UNSUPPORTED',
      userMessage: 'Dit bestandstype wordt niet ondersteund. Kies een jpg-, png-, webp- of gif-afbeelding.',
      technicalMessage: `unsupported avatar type: ${file.type}`,
      retryable: false,
      source: 'validation',
      correlationId: newCorrelationId(),
    })
  }
  if (file.size > AVATAR_MAX_BYTES) {
    return makeAppError({
      code: 'AVATAR_TOO_LARGE',
      userMessage: 'De afbeelding is te groot. Kies een afbeelding kleiner dan 2 MB.',
      technicalMessage: `avatar size ${file.size} exceeds ${AVATAR_MAX_BYTES}`,
      retryable: false,
      source: 'validation',
      correlationId: newCorrelationId(),
    })
  }
  return null
}

/**
 * Uploads the avatar to <userId>/avatar.<ext> (upsert) and returns the stored
 * object path. Throws AppError on validation or upload failure.
 */
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const invalid = validateAvatarFile(file)
  if (invalid) throw invalid
  const supabase = requireSupabase()
  const path = `${userId}/avatar.${extensionFor(file)}`
  try {
    const { error } = await supabase.storage.from(BUCKETS.avatars).upload(path, file, {
      upsert: true,
      contentType: file.type,
    })
    if (error) throw error
    return path
  } catch (error) {
    throw toAppError(error, 'storage')
  }
}

/**
 * Public URL for an avatar object path, or null when unavailable. The bucket
 * is public-read; `cacheBust` forces a refresh right after a new upload.
 */
export function getAvatarPublicUrl(avatarPath: string | null, cacheBust?: number): string | null {
  if (!avatarPath) return null
  const supabase = getSupabase()
  if (!supabase) return null
  const { data } = supabase.storage.from(BUCKETS.avatars).getPublicUrl(avatarPath)
  if (!data.publicUrl) return null
  return cacheBust ? `${data.publicUrl}?v=${cacheBust}` : data.publicUrl
}
